/**
 * "View in Onco EMR" menu action (PA form variant): validates base URL,
 * logs analytics, and opens the patient in a new tab.
 * Driven by Postgres-backed `generalOrderDetails` (`GeneralOrderDetails`).
 *
 * @module paFormThreeDotsMenu/handleViewInOncoEmr
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { GeneralOrderDetails } from "../../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { UseHealthCareFacilityData } from "../../../../hooks/useHealthCareFacility";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import { ToastMessages } from "../../../toasts/toastMessages";

/** Parameters for {@link handleViewInOncoEmr}. */
export interface HandleViewInOncoEmrParams {
  generalOrderDetails: GeneralOrderDetails;
  healthCareFacilityData: UseHealthCareFacilityData;
  orgId: string;
  userId: string;
  userEmail: string;
  payerName: string;
  screenName: ScreenNamesForBigQuery;
}

/**
 * Handles the "View in Onco EMR" action for the PA form three-dots menu.
 * Opens the patient's record in the Onco EMR system in a new browser tab.
 *
 * @param params - Parameters for the action
 */
export const handleViewInOncoEmr = ({
  generalOrderDetails,
  healthCareFacilityData,
  orgId,
  userId,
  userEmail,
  payerName,
  screenName,
}: HandleViewInOncoEmrParams): void => {
  if (
    !healthCareFacilityData.data?.onco_emr_base_url ||
    healthCareFacilityData.loading
  ) {
    toast.error(ToastMessages.ONCO_EMR_BASE_URL_NOT_FOUND);
    return;
  }

  apiWorkerManager.enqueueAndProcess({
    patient_id:
      generalOrderDetails?.demographics?.patient_id ??
      generalOrderDetails?.demographics?.patient_mrn ??
      "",
    order_id: generalOrderDetails?.order_id ?? "",
    org_id: orgId ?? "",
    event_name: EventsName.VIEW_IN_ONCO_EMR_CLICKED,
    user_id: userId,
    user_email: userEmail,
    additional_data: {
      screen_name: screenName,
      payer_name: payerName,
    },
  });

  const patientIdFromDb =
    generalOrderDetails?.demographics?.patient_fhir_identifier ?? "";
  const finalPatientId = patientIdFromDb.split(".")[0].replaceAll("--", "_");
  const finalUrl = `${healthCareFacilityData.data?.onco_emr_base_url ?? ""}/nav/demographics?PID=${finalPatientId}&locationId=LH_0673T0AXW8EFAE0WHC2E`;

  window.open(finalUrl, "_blank");
};
